import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow, SubRow, InlineInput,
    PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';

const DUMMY_DATA = {
    settings: {
        moderation: true, replies: true, characterLimit: true, charLimitValue: 200,
        labels: true, labelOptions: ['Technical', 'Product', 'General', 'Other'],
        downvotes: false, pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null,
    },
};

const AudienceQA = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const [settings, setSettings] = useState({
        moderation: false,
        replies: true,
        characterLimit: true,
        charLimitValue: 160,
        labels: false,
        labelOptions: ['Technical', 'General', 'Other'],
        downvotes: false,
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (key, val) => setSettings((s) => ({ ...s, [key]: val }));

    const right = (
        <>
            <SettingsGroup title="Q&A Settings">
                {/* Moderation */}
                <SettingsRow
                    label="Moderation"
                    description="Review incoming questions before they appear"
                >
                    <Toggle checked={settings.moderation} onChange={(e) => set('moderation', e.target.checked)} />
                </SettingsRow>

                {/* Replies */}
                <SettingsRow
                    label="Replies"
                    description="Allow participants to reply to questions"
                >
                    <Toggle checked={settings.replies} onChange={(e) => set('replies', e.target.checked)} />
                </SettingsRow>

                {/* Character Limit */}
                <SettingsRow
                    label="Character Limit"
                    description="Set the question maximum character limit"
                >
                    <Toggle checked={settings.characterLimit} onChange={(e) => set('characterLimit', e.target.checked)} />
                </SettingsRow>
                {settings.characterLimit && (
                    <SubRow>
                        <span>Max characters:</span>
                        <InlineInput
                            type="number"
                            value={settings.charLimitValue}
                            onChange={(e) => set('charLimitValue', Math.min(1000, Math.max(20, Number(e.target.value) || 20)))}
                            min={20}
                            max={1000}
                        />
                    </SubRow>
                )}

                {/* Labels */}
                <SettingsRow
                    label="Labels"
                    description="Categorize and organize questions using labels"
                >
                    <Toggle checked={settings.labels} onChange={(e) => set('labels', e.target.checked)} />
                </SettingsRow>
                {settings.labels && (
                    <SubRow>
                        <span>Label options (comma-separated):</span>
                        <input
                            type="text"
                            className={styles.inlineInput}
                            value={Array.isArray(settings.labelOptions) ? settings.labelOptions.join(', ') : (settings.labelOptions || 'Technical, General, Other')}
                            onChange={(e) => {
                            const arr = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                            set('labelOptions', arr.length ? arr : ['Technical', 'General', 'Other']);
                        }}
                            placeholder="Technical, General, Other"
                        />
                    </SubRow>
                )}

                {/* Downvotes */}
                <SettingsRow
                    label="Downvotes"
                    description="Allow participants to downvote questions. Upvotes are always on."
                >
                    <Toggle checked={settings.downvotes} onChange={(e) => set('downvotes', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their question after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
            {settings.imageUrl && (
                <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
            )}
            <div className={styles.questionContent}>
                <div className={styles.questionLabel}>Audience Q&amp;A</div>
                <p className={styles.activityDesc}>
                    Your audience can submit and upvote questions in real-time during your session.
                    Use the settings panel to control moderation, replies, and more.
                </p>
            </div>
        </div>
    );

    const loadDummy = () => setSettings(DUMMY_DATA.settings);

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'audience-qa',
                title: 'Audience Q&A',
                content: { settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<MessageCircle size={20} />}
            color="var(--cat-blue)"
            title="Audience Q&A"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            onSave={() => saveContent({ settings })}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default AudienceQA;
